import { ChangePackageStep } from '@/features/dashboard/bookingChanges/routes/changePackageSearch.ts';
import useChangePackageFlow from '@/features/dashboard/bookingChanges/hooks/useChangePackageFlow.ts';
import {
  ChangePackageAcceptanceStepSkeleton,
  ChangePackageSettlementStepSkeleton,
  ChangePackageSummaryStepSkeleton,
} from '@/features/dashboard/bookingChanges/components/flow';
import ChangePackageFlowStepGate from '@/features/dashboard/bookingChanges/components/flow/ChangePackageFlowStepGate.tsx';
import ChangePackageSummaryStep from './ChangePackageSummaryStep';
import ChangePackageSettlementStep from './ChangePackageSettlementStep';
import ChangePackageAcceptanceStep from './ChangePackageAcceptanceStep';

/**
 * Trzyetapowe wprowadzanie paczki: podsumowanie (W1) → rozliczenie (W2) →
 * podgląd strony akceptacji (W3).
 *
 * Strona jest samą kompozycją: `?step=` rozstrzyga, który krok widać, a całą
 * obsługę przepływu — zapytania, wybory operatora, wprowadzenie paczki —
 * trzyma `useChangePackageFlow`.
 */
const ChangePackageFlowPage = () => {
  const {
    step,
    summaryQuery,
    settlementQuery,
    acceptanceQuery,
    resendChangeId,
    selection,
    goToStep,
    goToBooking,
    submit,
    isSubmitting,
  } = useChangePackageFlow();

  const renderStep = () => {
    if (step === ChangePackageStep.SETTLEMENT) {
      return (
        <ChangePackageFlowStepGate
          isLoading={settlementQuery.isLoading}
          skeleton={<ChangePackageSettlementStepSkeleton />}
          data={settlementQuery.settlement}
          error={settlementQuery.error}
          errorTitle="Nie udało się przeliczyć rozliczenia"
          emptyTitle="Nie udało się przeliczyć rozliczenia"
          emptyDescription="Harmonogram rat i lista dokumentów nie są dostępne dla tej paczki. Wróć do edycji i spróbuj ponownie."
        >
          {(settlement) => (
            <ChangePackageSettlementStep
              settlement={settlement}
              option={selection.settlementOption}
              onOptionChange={selection.setSettlementOption}
              route={selection.changeRoute}
              onRouteChange={selection.setChangeRoute}
              selectedDocumentIds={selection.selectedDocumentIds}
              onToggleDocument={selection.toggleDocument}
              correctionDueDate={selection.correctionDueDate}
              onCorrectionDueDateChange={selection.setCorrectionDueDate}
              correctionPaymentMethods={selection.correctionPaymentMethods}
              onToggleCorrectionPaymentMethod={
                selection.toggleCorrectionPaymentMethod
              }
              onBack={() => void goToStep(ChangePackageStep.SUMMARY)}
              onSubmit={() => void submit()}
              isSubmitting={isSubmitting}
            />
          )}
        </ChangePackageFlowStepGate>
      );
    }

    if (step === ChangePackageStep.ACCEPTANCE) {
      return (
        <ChangePackageFlowStepGate
          isLoading={acceptanceQuery.isLoading}
          skeleton={<ChangePackageAcceptanceStepSkeleton />}
          data={acceptanceQuery.view}
          error={acceptanceQuery.error}
          errorTitle="Nie udało się przeliczyć kwot paczki"
          emptyTitle="Brak paczki do pokazania"
          emptyDescription="Ta rezerwacja nie ma otwartej paczki, więc nie ma czego pokazać zapisującemu."
        >
          {(view) => (
            <ChangePackageAcceptanceStep
              view={view}
              resendChangeId={resendChangeId}
            />
          )}
        </ChangePackageFlowStepGate>
      );
    }

    return (
      <ChangePackageFlowStepGate
        isLoading={summaryQuery.isLoading}
        skeleton={<ChangePackageSummaryStepSkeleton />}
        data={summaryQuery.summary}
        error={summaryQuery.error}
        errorTitle="Nie udało się przeliczyć kwot paczki"
        emptyTitle="Brak otwartego szkicu"
        emptyDescription="Ta rezerwacja nie ma paczki do wprowadzenia. Wejdź w tryb edycji i zmień coś, żeby szkic powstał."
      >
        {(summary) => (
          <ChangePackageSummaryStep
            summary={summary}
            onBack={() => void goToBooking()}
            onOpenBooking={() => void goToBooking()}
            onNext={() => void goToStep(ChangePackageStep.SETTLEMENT)}
          />
        )}
      </ChangePackageFlowStepGate>
    );
  };

  return (
    <div className="flex w-full max-w-[1356px] flex-col gap-4">
      {renderStep()}
    </div>
  );
};

export default ChangePackageFlowPage;
